<script setup>
import { ref, inject, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { VSwitch } from 'vuetify/components/VSwitch';
import FormSectionTitle from '@/components/widgets/forms/FormSectionTitle.vue';

const backendApi = inject('backendApi');
const { t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['update', 'update:focused']);
const props = defineProps({
    selectedRoles: {
        type: Array,
        default: () => []
    },
    mode: {
        type: String,
        default: 'edit'
    }
});

const selectedRolesRef = ref([]);
const loading = ref(true);
const roles = ref([]);
const showRetryButton = ref(false);

const groupVisibilities = ref([]);

const getRoles = () => {
    cleanData();

    backendApi()
        .auth()
        .getRoles()
        .then(success, fail)
        .finally(() => (loading.value = false));

    function success(response) {
        roles.value = response.data;
        initializeSelectedRoles();
    }

    function fail() {
        showRetryButton.value = true;
    }
};

const initializeSelectedRoles = () => {
    selectedRolesRef.value = props.selectedRoles;
};

const cleanData = () => {
    roles.value = [];
    loading.value = true;
    showRetryButton.value = false;
};

const toggleRole = () => {
    emit('update', selectedRolesRef.value);
};

const groupedRoles = computed(() =>
    roles.value.reduce((groups, role) => {
        const category = role.category || t('No Category');
        if (!groups[category]) groups[category] = [];
        groups[category].push(role);
        return groups;
    }, {})
);

watch(groupedRoles, (groups) => {
    groupVisibilities.value = Object.keys(groups).map(() => true);
});

onMounted(() => getRoles());

watch(
    () => props.selectedRoles,
    (newRoles) => {
        selectedRolesRef.value = newRoles;
    },
    { immediate: true }
);
</script>

<template>
    <div>
        <div
            v-for="(group, category, idx) in groupedRoles"
            :key="category"
            class="mb-4"
        >
            <FormSectionTitle
                :title="category"
                v-model="groupVisibilities[idx]"
            />
            <v-row v-show="groupVisibilities[idx]" class="mt-3">
                <v-col v-for="role in group" :key="role.id" cols="12" md="3">
                    <VSwitch
                        v-model="selectedRolesRef"
                        color="primary"
                        :value="role.id"
                        :label="role.description"
                        :disabled="props.mode === 'view'"
                        @change="toggleRole(role)"
                    />
                </v-col>
            </v-row>
        </div>
        <div v-if="showRetryButton">
            <v-btn @click="getRoles">Retry</v-btn>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.title-container {
    position: relative;
    margin: 3px 0 0px;
    padding-left: 8px;
}

.section-label {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.87);
    line-height: 24px;
}
</style>
